import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Monitor, Loader, CheckCircle, AlertCircle } from 'lucide-react';
import { apiClient } from '../utils/api';
import { CookieUtils, KIOSK_COOKIES } from '../utils/cookies';

export const KioskActivatePage: React.FC = () => {
  const [pairingCode, setPairingCode] = useState<string | null>(null);
  const [status, setStatus] = useState<'loading' | 'pending' | 'approved' | 'expired' | 'error'>('loading');
  const [error, setError] = useState<string | null>(null);
  const [expiresIn, setExpiresIn] = useState<number>(900); // 15 minutes in seconds
  const navigate = useNavigate();

  // Check for existing kiosk pairing on mount before generating new code
  useEffect(() => {
    // Only check for kiosk-specific tokens, NOT regular auth_token
    // A normal login auth_token does not mean the kiosk is paired
    const localKioskToken = localStorage.getItem('kiosk_device_token');
    const cookieKioskToken = document.cookie.split('; ').find(row => row.startsWith('kiosk_device_token='))?.split('=')[1];

    if (localKioskToken || cookieKioskToken) {
      console.log('[KioskActivate] Found existing kiosk pairing, redirecting to kiosk');
      navigate('/kiosk');
      return;
    }

    // No existing pairing found, generate new pairing code
    if (!pairingCode) {
      console.log('[KioskActivate] No existing pairing found, generating new code');
      requestPairingCode();
    }
  }, []);

  // Poll for approval status
  useEffect(() => {
    if (!pairingCode || status !== 'pending') return;

    const pollInterval = setInterval(async () => {
      try {
        const response = await apiClient.checkKioskPairing(pairingCode);

        if (response.status === 'approved' && response.token) {
          setStatus('approved');
          clearInterval(pollInterval);

          // The device token authenticates every kiosk API call (sent as the
          // bearer token, accepted by Phoenix's kiosk_auth pipeline). Cookies
          // back up localStorage across browser restarts.
          const pairedAt = new Date().toISOString();
          localStorage.setItem('kiosk_device_token', response.token);
          localStorage.setItem('kiosk_device_id', pairingCode);
          localStorage.setItem('kiosk_device_name', 'Kiosk Display');
          localStorage.setItem('kiosk_paired_at', pairedAt);
          localStorage.setItem('auth_token', response.token);

          CookieUtils.setCookie(KIOSK_COOKIES.DEVICE_TOKEN, response.token, 365);
          CookieUtils.setCookie(KIOSK_COOKIES.DEVICE_ID, pairingCode, 365);
          CookieUtils.setCookie(KIOSK_COOKIES.DEVICE_NAME, 'Kiosk Display', 365);
          CookieUtils.setCookie(KIOSK_COOKIES.PAIRED_AT, pairedAt, 365);
          CookieUtils.setCookie('auth_token', response.token, 365);

          setTimeout(() => {
            navigate('/kiosk');
          }, 1000);
        } else if (response.status === 'expired') {
          setStatus('expired');
          clearInterval(pollInterval);
        } else if (response.status === 'consumed' || response.status === 'revoked') {
          // Approved, but the one-time token went to another poll (or the
          // device was revoked): this screen cannot finish pairing.
          setError('This code can no longer be used. Generate a new code.');
          setStatus('error');
          clearInterval(pollInterval);
        }
      } catch (err: any) {
        console.error('Error checking pairing status:', err);
        // Continue polling even on error
      }
    }, 3000); // Poll every 3 seconds

    return () => clearInterval(pollInterval);
  }, [pairingCode, status, navigate]);

  // Countdown timer for expiration
  useEffect(() => {
    if (status !== 'pending') return;

    const timer = setInterval(() => {
      setExpiresIn(prev => {
        if (prev <= 1) {
          setStatus('expired');
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [status]);

  const requestPairingCode = async () => {
    try {
      setStatus('loading');
      setError(null);

      const response = await apiClient.requestKioskPairing();

      setPairingCode(response.code);
      setExpiresIn(response.expires_in || 900);
      setStatus('pending');
    } catch (err: any) {
      console.error('Error requesting pairing code:', err);
      setError(err.message || 'Failed to generate pairing code');
      setStatus('error');
    }
  };

  const formatTime = (seconds: number): string => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-900 via-purple-900 to-pink-900 flex items-center justify-center p-6">
      <div className="max-w-3xl w-full">
        {/* Header */}
        <div className="text-center mb-12">
          <div className="flex justify-center mb-6">
            <div className="bg-white/10 backdrop-blur-md p-6 rounded-2xl border border-white/20">
              <Monitor className="w-24 h-24 text-white" />
            </div>
          </div>
          <h1 className="text-6xl font-bold text-white mb-4">
            Activate Kiosk
          </h1>
        </div>

        {/* Status Card */}
        <div className="bg-white/10 backdrop-blur-md rounded-3xl p-12 border border-white/20">
          {status === 'loading' && (
            <div className="text-center">
              <Loader className="w-16 h-16 text-white animate-spin mx-auto mb-6" />
              <p className="text-2xl text-white">Generating activation code...</p>
            </div>
          )}

          {status === 'pending' && pairingCode && (
            <div className="text-center">
              <p className="text-3xl text-white/90 mb-8">
                Enter this code on your phone or computer:
              </p>

              {/* Pairing Code Display */}
              <div className="bg-white/20 backdrop-blur-md rounded-2xl p-8 mb-8 border-4 border-white/40">
                <div className="text-8xl font-mono font-bold text-white tracking-wider mb-4">
                  {pairingCode.replace(/^K-/, '')}
                </div>
                <div className="text-xl text-white/80">
                  Expires in {formatTime(expiresIn)}
                </div>
              </div>

              {/* Instructions */}
              <div className="space-y-4 text-left">
                <div className="flex items-start gap-4 bg-white/10 rounded-xl p-4">
                  <div className="bg-blue-500 text-white rounded-full w-8 h-8 flex items-center justify-center flex-shrink-0 font-bold">
                    1
                  </div>
                  <p className="text-xl text-white">
                    On your phone, go to <span className="font-mono bg-white/20 px-3 py-1 rounded">{window.location.host}/pair</span>
                  </p>
                </div>
                <div className="flex items-start gap-4 bg-white/10 rounded-xl p-4">
                  <div className="bg-blue-500 text-white rounded-full w-8 h-8 flex items-center justify-center flex-shrink-0 font-bold">
                    2
                  </div>
                  <p className="text-xl text-white">
                    Enter the code above
                  </p>
                </div>
                <div className="flex items-start gap-4 bg-white/10 rounded-xl p-4">
                  <div className="bg-blue-500 text-white rounded-full w-8 h-8 flex items-center justify-center flex-shrink-0 font-bold">
                    3
                  </div>
                  <p className="text-xl text-white">
                    Kiosk will activate automatically
                  </p>
                </div>
              </div>

              {/* Waiting Indicator */}
              <div className="mt-8 flex items-center justify-center gap-3 text-white/70">
                <Loader className="w-6 h-6 animate-spin" />
                <span className="text-lg">Waiting for approval...</span>
              </div>
            </div>
          )}

          {status === 'approved' && (
            <div className="text-center">
              <CheckCircle className="w-24 h-24 text-green-400 mx-auto mb-6" />
              <p className="text-4xl text-white font-bold mb-4">Kiosk Activated!</p>
              <p className="text-2xl text-white/80">Loading dashboard...</p>
            </div>
          )}

          {status === 'expired' && (
            <div className="text-center">
              <AlertCircle className="w-24 h-24 text-orange-400 mx-auto mb-6" />
              <p className="text-4xl text-white font-bold mb-4">Code Expired</p>
              <p className="text-2xl text-white/80 mb-8">The activation code has expired</p>
              <button
                onClick={requestPairingCode}
                className="px-8 py-4 bg-blue-500 hover:bg-blue-600 text-white rounded-xl text-xl font-bold transition-colors"
              >
                Generate New Code
              </button>
            </div>
          )}

          {status === 'error' && (
            <div className="text-center">
              <AlertCircle className="w-24 h-24 text-red-400 mx-auto mb-6" />
              <p className="text-4xl text-white font-bold mb-4">Error</p>
              <p className="text-2xl text-white/80 mb-8">{error}</p>
              <button
                onClick={requestPairingCode}
                className="px-8 py-4 bg-blue-500 hover:bg-blue-600 text-white rounded-xl text-xl font-bold transition-colors"
              >
                Try Again
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default KioskActivatePage;
